import { useState } from 'react'
import { Button, DataTable, Icon, IconButton, Money, confirmDialog, type ColumnDef } from '../../components'
import { useDeletePlanned, usePlanned } from '../../lib/api/planning'
import type { PlannedTransaction } from '../../lib/api/planning'
import { PlannedDrawer } from './planned-drawer'
import { CompleteModal } from './complete-modal'
import { useMe } from '../../lib/use-me'
import { canDelete } from '../../lib/permissions'

const TYPE_LABEL: Record<string, string> = {
  income: 'Прихід',
  expense: 'Витрата',
  transfer: 'Переказ',
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Очікує',
  completed: 'Виконано',
  cancelled: 'Скасовано',
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'var(--warning-fg)',
  completed: 'var(--success-fg)',
  cancelled: 'var(--fg-4)',
}

export function PlannedTab() {
  const me = useMe()
  const allowDelete = canDelete(me.data?.role)

  const [filterStatus, setFilterStatus] = useState<'pending' | 'completed' | 'cancelled' | ''>('')
  const [drawerItem, setDrawerItem] = useState<PlannedTransaction | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [completeItem, setCompleteItem] = useState<PlannedTransaction | null>(null)

  const list = usePlanned({ status: filterStatus || undefined })
  const deletePlanned = useDeletePlanned()

  const items = list.data?.items ?? []

  const openCreate = () => {
    setDrawerItem(null)
    setDrawerOpen(true)
  }

  const openEdit = (item: PlannedTransaction) => {
    setDrawerItem(item)
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setDrawerItem(null)
  }

  const columns: ColumnDef<PlannedTransaction>[] = [
    {
      id: 'date',
      header: 'Дата',
      width: 120,
      cellClassName: 'fg2',
      sortValue: (item) => item.plannedDate,
      cell: (item) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{item.plannedDate}</span>
      ),
    },
    {
      id: 'type',
      header: 'Тип',
      width: 100,
      sortValue: (item) => item.type,
      cell: (item) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Icon
            name={
              item.type === 'income'
                ? 'arrow-down-left'
                : item.type === 'expense'
                  ? 'arrow-up-right'
                  : 'arrow-left-right'
            }
            size={12}
            color={
              item.type === 'income'
                ? 'var(--success-fg)'
                : item.type === 'transfer'
                  ? 'var(--accent)'
                  : 'var(--fg-3)'
            }
          />
          {TYPE_LABEL[item.type]}
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Сума',
      width: 140,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (item) => item.amount,
      cell: (item) => <Money amount={item.amount} currency={item.currency} />,
    },
    {
      id: 'description',
      header: 'Опис',
      cellClassName: 'fg2',
      sortValue: (item) => item.description ?? null,
      cell: (item) => item.description ?? <span className="fg3">—</span>,
    },
    {
      id: 'status',
      header: 'Статус',
      width: 110,
      sortValue: (item) => item.status,
      cell: (item) => (
        <span style={{ fontSize: 12, color: STATUS_COLOR[item.status] }}>
          {STATUS_LABEL[item.status]}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 100,
      pinned: true,
      cell: (item) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          {item.status === 'pending' && (
            <IconButton
              icon="check-circle"
              tooltip="Виконати"
              onClick={() => setCompleteItem(item)}
            />
          )}
          <IconButton icon="pencil" tooltip="Редагувати" onClick={() => openEdit(item)} />
          {allowDelete && (
            <IconButton
              icon="trash-2"
              tooltip="Видалити"
              onClick={async () => {
                const ok = await confirmDialog({
                  title: 'Видалити планову операцію?',
                  body: 'Цю дію не можна скасувати.',
                  tone: 'danger',
                })
                if (ok) deletePlanned.mutate(item.id)
              }}
            />
          )}
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--fg-3)' }}>Статус:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="input"
            style={{ width: 140 }}
          >
            <option value="">Всі</option>
            <option value="pending">Очікує</option>
            <option value="completed">Виконано</option>
            <option value="cancelled">Скасовано</option>
          </select>
        </div>
        <Button variant="primary" size="sm" icon="plus" onClick={openCreate}>
          Нова операція
        </Button>
      </div>

      {list.isLoading && <SkeletonRows />}

      {!list.isLoading && items.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
            Планових операцій не знайдено.
          </div>
          <Button variant="primary" size="sm" icon="plus" onClick={openCreate}>
            Додати першу
          </Button>
        </div>
      )}

      {!list.isLoading && items.length > 0 && (
        <div className="card">
          <DataTable
            tableKey="planned"
            columns={columns}
            rows={items}
            rowKey={(item) => item.id}
          />
        </div>
      )}

      {drawerOpen && (
        <PlannedDrawer
          item={drawerItem}
          onClose={closeDrawer}
        />
      )}

      {completeItem && (
        <CompleteModal
          planned={completeItem}
          onClose={() => setCompleteItem(null)}
        />
      )}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="card" style={{ padding: 20 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 36,
            background: 'var(--bg-3)',
            borderRadius: 4,
            marginBottom: 6,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  )
}
